import { notification } from 'antd';
import type { AxiosError } from 'axios';
import axios from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { AppCode } from '../../../enum';
import { APIStatus } from '../../../enum';
import { removeEmpty } from '../../../helpers/utils.helper';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import type { FilteredInfo, RequestParams, TableChange } from '../../../models/table';
import { SortedInfo } from '../../../models/table';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';

const { logError } = RaygunErrorHandlerService();

type BooleanStr = 'true' | 'false';

export type GuestFeedback = {
  id: string;
  chat_id: string | null;
  app_code: AppCode | null;
  timestamp: string;
  source: 'Chat' | 'Feedback';
  feedback: string | null;
  training: BooleanStr | null;
  advocate_name: string | null;
  email: string | null;
  first_name: string | null;
  total_count: number;
  rating: string;
  helpful: BooleanStr | null;
  guest_id: string;
  guest_org_name: string;
  advocate_org_name: string;
  guest_org_timezone_name: string;
  advocate_org_timezone_name: string;
  starred: boolean;
};

type GuestFeedbackSliceType = {
  guestFeedback: GuestFeedback[];
  guestFeedbackParam: RequestParams<GuestFeedback>;
  apiStatus: APIStatus;
};

const initialState: GuestFeedbackSliceType = {
  guestFeedback: [],
  guestFeedbackParam: {
    pagination: {
      current: 1,
      pageSize: 50,
      defaultPageSize: 50,
    },
    sortedInfo: new SortedInfo<GuestFeedback>('timestamp', 'descend'),
    filteredInfo: {},
  },
  apiStatus: APIStatus.IDLE,
};

export const changeGuestFeedbackRating = createAsyncThunk<string, { id: string; value: string }, AsyncThunkConfig>(
  'guestFeedback/changeGuestFeedbackRating',
  async (rating, thunkAPI) => {
    try {
      const response = (await axios.post('/v0_set_guest_feedback_rating', rating)) as string; // id;
      thunkAPI.dispatch(fetchGuestFeedback());
      if (response) {
        notification.success({
          title: 'Success',
          description: 'Rating has been successfully changed.',
        });
      }
      return response;
    } catch (e) {
      logError(e, ['guestFeedbackSlice', 'changeGuestFeedbackRating']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export type GuestFeedbackStarred = { id: string; starred: boolean };
export const changeGuestFeedbackStarred = createAsyncThunk<string, GuestFeedbackStarred, AsyncThunkConfig>(
  'guestFeedback/changeGuestFeedbackStarred',
  async (data, thunkAPI) => {
    try {
      const response = (await axios.post('/v0_star_guest_feedback', data)) as string; // id;
      thunkAPI.dispatch(fetchGuestFeedback());
      if (response) {
        notification.success({
          title: 'Success',
          description: `Feedback was ${data.starred ? 'starred' : 'unstarred'} successfully.`,
        });
      }
      return response;
    } catch (e) {
      logError(e, ['guestFeedbackSlice', 'changeGuestFeedbackStarred']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export type FeedbackContent = { guest_id: string; chat_id: string; email_address: string; feedback: string };
export const logEmailFeedbackClicked = createAsyncThunk<number, FeedbackContent, AsyncThunkConfig>(
  'guestFeedback/logEmailFeedbackClicked',
  async (feedbackContent, thunkAPI) => {
    try {
      const response = (await axios.post('/v0_log_feedback_email', feedbackContent)) as number; // id;
      return response;
    } catch (e) {
      logError(e, ['guestFeedbackSlice', 'logEmailFeedbackClicked']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchGuestFeedback = createAsyncThunk<GuestFeedback[], undefined, AsyncThunkConfig>(
  'guestFeedback/fetchGuestFeedback',
  async (_, thunkAPI) => {
    try {
      const guestFeedbackParam = thunkAPI.getState().guestFeedbackSlice.guestFeedbackParam;
      const response = (await axios.post('v0_get_guest_feedback', guestFeedbackParam)) as GuestFeedback[];
      return response ?? [];
    } catch (e) {
      logError(e, ['guestFeedbackSlice', 'fetchGuestFeedback']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const guestFeedbackSlice = createSlice({
  name: 'guestFeedback',
  initialState,
  reducers: {
    handleFeedbackTableChange: (state, action: PayloadAction<TableChange<GuestFeedback>>) => {
      const filters = action.payload.filters as FilteredInfo;
      const sorter = action.payload.sorter as SortedInfo<GuestFeedback>;
      state.guestFeedbackParam.pagination = action.payload.pagination;
      state.guestFeedbackParam.sortedInfo = sorter;
      state.guestFeedbackParam.filteredInfo = removeEmpty(filters);
    },
    clearFilter: (state) => {
      state.guestFeedbackParam.pagination = initialState.guestFeedbackParam.pagination;
      state.guestFeedbackParam.sortedInfo = initialState.guestFeedbackParam.sortedInfo;
      state.guestFeedbackParam.filteredInfo = initialState.guestFeedbackParam.filteredInfo;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGuestFeedback.pending, (state, _action) => {
        state.apiStatus = APIStatus.PENDING;
      })
      .addCase(fetchGuestFeedback.fulfilled, (state, action) => {
        state.guestFeedback = action.payload;
        state.apiStatus = APIStatus.FULFILLED;
      })
      .addCase(fetchGuestFeedback.rejected, (state, _action) => {
        state.apiStatus = APIStatus.ERROR;
      });
  },
});

export const { handleFeedbackTableChange, clearFilter } = guestFeedbackSlice.actions;
